import React, { useEffect, useRef, useState } from 'react';
import {
  Bell,
  Briefcase,
  ChevronDown,
  Compass,
  Home,
  Inbox,
  LayoutDashboard,
  LogIn,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import {
  PRODUCT_WORKSPACE_MODES,
  WORKSPACE_VARIANTS,
} from '../../config/workspaceVariants';
import {
  ApplicationSwitcherMenu,
  NotificationsMenu,
  ProfileMenu,
  WorkspaceSwitcherMenu,
} from './HeaderMenus';

const WORKSPACE_MENU_ID = 'testsuite-workspace-switcher';
const NOTIFICATIONS_MENU_ID = 'testsuite-notifications';
const PROFILE_MENU_ID = 'testsuite-account-menu';

const WORKSPACE_TONE_CLASSES = {
  pdf: 'header-tone-blue',
  requirements: 'header-tone-violet',
  'api-testing': 'header-tone-sky',
  'excel-testcases': 'header-tone-emerald',
  'feature-capture': 'header-tone-teal',
};

const PRODUCT_MODE_CONFIG = Object.fromEntries(
  PRODUCT_WORKSPACE_MODES.map((mode) => {
    const variant = WORKSPACE_VARIANTS[mode];
    return [
      mode,
      {
        label: variant.label,
        icon: variant.icon,
        toneClass: WORKSPACE_TONE_CLASSES[mode],
      },
    ];
  })
);

const MODE_CONFIG = {
  landing: {
    label: 'Home',
    icon: Compass,
    toneClass: 'header-tone-blue',
  },
  ...PRODUCT_MODE_CONFIG,
  admin: {
    label: 'Admin Portal',
    icon: ShieldCheck,
    toneClass: 'header-tone-emerald',
  },
  requests: {
    label: 'Request Center',
    icon: Inbox,
    toneClass: 'header-tone-amber',
  },
  applications: {
    label: 'Applications',
    icon: Briefcase,
    toneClass: 'header-tone-emerald',
  },
  application: {
    label: 'Application',
    icon: Briefcase,
    toneClass: 'header-tone-emerald',
  },
};

const WORKSPACE_MODES = PRODUCT_WORKSPACE_MODES.map((mode) => {
  const variant = WORKSPACE_VARIANTS[mode];
  return {
    mode,
    label: variant.label,
    description: variant.description,
    icon: variant.icon,
    toneClass: WORKSPACE_TONE_CLASSES[mode],
  };
});

const formatRoleLabel = (role) => {
  const value = String(role || '').trim();
  if (!value) return '';

  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getInitials = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return 'TS';
  const parts = normalized
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (!parts.length) return 'TS';
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
};

const normalizeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

export const Header = ({
  currentMode,
  onHome,
  onAdmin,
  onOpenDashboard,
  onSecurity,
  onLogin,
  onLogout,
  onOpenRequests,
  onOpenRequestItem,
  onOpenActivityItem,
  onSwitchWorkspace,
  applications = [],
  activeApplication = null,
  applicationsLoading = false,
  isApplicationContext = false,
  onSwitchApplication = null,
  onOpenApplications = null,
  isAdmin = false,
  isAuthenticated = false,
  currentUser = null,
  notificationSummary = null,
  notificationItems = [],
  activitySummary = null,
  activityItems = [],
  notificationsLoading = false,
  onRefreshNotifications = null,
  onMarkNotificationsRead = null,
}) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const workspaceMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const profileMenuRef = useRef(null);
  const workspaceTriggerRef = useRef(null);
  const notificationsTriggerRef = useRef(null);
  const profileTriggerRef = useRef(null);

  const isLanding = currentMode === 'landing' || currentMode === 'home';
  const currentModeConfig = MODE_CONFIG[currentMode];
  const ModeIcon = currentModeConfig?.icon;
  const isWorkspaceMode = WORKSPACE_MODES.some((item) => item.mode === currentMode);
  const userLabel = currentUser?.name || currentUser?.email || 'Signed in';
  const userEmail = String(currentUser?.email || '').trim();
  const userRoleLabel = formatRoleLabel(currentUser?.role);
  const userInitials = getInitials(userLabel);

  const summary = notificationSummary || {};
  const pendingCount = normalizeCount(summary?.total_pending);
  const pendingBrd = normalizeCount(summary?.brd_pending);
  const pendingSharing = normalizeCount(summary?.sharing_pending);
  const pendingTestCases = normalizeCount(summary?.test_case_pending);
  const unreadUpdates = normalizeCount(activitySummary?.unread_count);
  const totalNotificationCount = pendingCount + unreadUpdates;
  const visualNotificationCount = totalNotificationCount > 99 ? '99+' : totalNotificationCount;
  const requestItems = Array.isArray(notificationItems) ? notificationItems.slice(0, 6) : [];
  const updateItems = Array.isArray(activityItems) ? activityItems.slice(0, 5) : [];

  useEffect(() => {
    if (!openMenu) return undefined;

    const handlePointerDown = (event) => {
      const activeAnchor = openMenu === 'workspace'
        ? workspaceMenuRef.current
        : openMenu === 'notifications'
          ? notificationsRef.current
          : profileMenuRef.current;
      if (activeAnchor && !activeAnchor.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      const activeTrigger = openMenu === 'workspace'
        ? workspaceTriggerRef.current
        : openMenu === 'notifications'
          ? notificationsTriggerRef.current
          : profileTriggerRef.current;
      setOpenMenu(null);
      window.requestAnimationFrame(() => activeTrigger?.focus());
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openMenu]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const panelId = openMenu === 'workspace'
      ? WORKSPACE_MENU_ID
      : openMenu === 'notifications'
        ? NOTIFICATIONS_MENU_ID
        : PROFILE_MENU_ID;
    const animationFrame = window.requestAnimationFrame(() => {
      document
        .getElementById(panelId)
        ?.querySelector('button:not(:disabled)')
        ?.focus();
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [openMenu]);

  useEffect(() => {
    setOpenMenu(null);
  }, [currentMode, isAuthenticated]);

  const toggleMenu = (menu) => {
    setOpenMenu((current) => (current === menu ? null : menu));
  };

  const handleMenuBlur = (menu, event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setOpenMenu((current) => (current === menu ? null : current));
  };

  return (
    <header className="app-header">
      <div className="app-header-inner">
        <button
          type="button"
          onClick={onHome}
          className="app-header-brand"
          aria-label="Go to TestSuite AI home"
        >
          {logoFailed ? (
            <span className="app-header-logo app-header-logo-fallback" aria-hidden="true">
              <Sparkles className="h-5 w-5" />
            </span>
          ) : (
            <img
              src="/metaz-logo.png"
              alt=""
              className="app-header-logo"
              style={{ objectPosition: '50% 32%' }}
              onError={() => setLogoFailed(true)}
            />
          )}
          <span className="app-header-brand-copy">
            <strong>TestSuite AI</strong>
            <span>by MetaZ</span>
          </span>
        </button>

        <nav className="app-header-actions" aria-label="Primary navigation">
          {!isLanding && currentModeConfig && ModeIcon && (
            <div
              className="header-menu-anchor"
              ref={workspaceMenuRef}
              onBlur={(event) => handleMenuBlur('workspace', event)}
            >
              {isApplicationContext && typeof onSwitchApplication === 'function' ? (
                <>
                  <button
                    ref={workspaceTriggerRef}
                    type="button"
                    onClick={() => toggleMenu('workspace')}
                    className={`header-control header-workspace-trigger header-tone-emerald ${
                      openMenu === 'workspace' ? 'is-open' : ''
                    }`}
                    aria-haspopup="dialog"
                    aria-expanded={openMenu === 'workspace'}
                    aria-controls={WORKSPACE_MENU_ID}
                    aria-label={`Switch application; current application: ${
                      activeApplication?.name || 'none selected'
                    }`}
                  >
                    <Briefcase className="header-control-icon" aria-hidden="true" />
                    <span className="header-control-label">
                      {activeApplication?.name || 'Applications'}
                    </span>
                    <ChevronDown
                      className={`header-control-chevron ${
                        openMenu === 'workspace' ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  {openMenu === 'workspace' && (
                    <ApplicationSwitcherMenu
                      id={WORKSPACE_MENU_ID}
                      applications={applications}
                      activeApplication={activeApplication}
                      loading={applicationsLoading}
                      onSelect={(application, isActive) => {
                        setOpenMenu(null);
                        if (!isActive) onSwitchApplication(application.id);
                      }}
                      onOpenAll={() => {
                        setOpenMenu(null);
                        onOpenApplications?.();
                      }}
                    />
                  )}
                </>
              ) : isWorkspaceMode && typeof onSwitchWorkspace === 'function' ? (
                <>
                  <button
                    ref={workspaceTriggerRef}
                    type="button"
                    onClick={() => toggleMenu('workspace')}
                    className={`header-control header-workspace-trigger ${currentModeConfig.toneClass} ${
                      openMenu === 'workspace' ? 'is-open' : ''
                    }`}
                    aria-haspopup="dialog"
                    aria-expanded={openMenu === 'workspace'}
                    aria-controls={WORKSPACE_MENU_ID}
                    aria-label={`Switch product area; current area: ${currentModeConfig.label}`}
                  >
                    <ModeIcon className="header-control-icon" aria-hidden="true" />
                    <span className="header-control-label">{currentModeConfig.label}</span>
                    <ChevronDown
                      className={`header-control-chevron ${
                        openMenu === 'workspace' ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  {openMenu === 'workspace' && (
                    <WorkspaceSwitcherMenu
                      id={WORKSPACE_MENU_ID}
                      currentMode={currentMode}
                      currentModeConfig={currentModeConfig}
                      items={WORKSPACE_MODES}
                      onSelect={(item, isActive) => {
                        setOpenMenu(null);
                        if (!isActive) onSwitchWorkspace(item.mode);
                      }}
                    />
                  )}
                </>
              ) : (
                <div
                  className={`header-control header-context-control ${currentModeConfig.toneClass}`}
                  aria-label={`Current area: ${currentModeConfig.label}`}
                >
                  <ModeIcon className="header-control-icon" aria-hidden="true" />
                  <span className="header-control-label">{currentModeConfig.label}</span>
                </div>
              )}
            </div>
          )}

          {!isLanding && (
            <button
              type="button"
              onClick={onHome}
              className="header-control header-neutral-control header-home-control"
              title="Go to home"
              aria-label="Go to home"
            >
              <Home className="header-control-icon header-icon-home" aria-hidden="true" />
              <span className="header-control-label header-secondary-label">Home</span>
            </button>
          )}

          {isAuthenticated && currentMode !== 'dashboard' && typeof onOpenDashboard === 'function' && (
            <button
              type="button"
              onClick={onOpenDashboard}
              className="header-control header-neutral-control header-dashboard-control"
              title="Open QA dashboard"
              aria-label="Open QA dashboard"
            >
              <LayoutDashboard className="header-control-icon" aria-hidden="true" />
              <span className="header-control-label header-secondary-label">Dashboard</span>
            </button>
          )}

          {isAuthenticated && isAdmin && currentMode !== 'admin' && (
            <button
              type="button"
              onClick={onAdmin}
              className="header-control header-neutral-control header-admin-control"
              title="Open admin portal"
              aria-label="Open admin portal"
            >
              <ShieldCheck className="header-control-icon header-icon-admin" aria-hidden="true" />
              <span className="header-control-label header-secondary-label">Admin</span>
            </button>
          )}

          {!isLanding && <span className="app-header-divider" aria-hidden="true" />}

          {isAuthenticated ? (
            <>
              <div
                className="header-menu-anchor"
                ref={notificationsRef}
                onBlur={(event) => handleMenuBlur('notifications', event)}
              >
                <button
                  ref={notificationsTriggerRef}
                  type="button"
                  onClick={() => toggleMenu('notifications')}
                  className={`header-control header-neutral-control header-notifications-trigger ${
                    openMenu === 'notifications' ? 'is-open' : ''
                  }`}
                  title="Requests and updates"
                  aria-label={`Notifications: ${pendingCount} pending requests and ${unreadUpdates} unread updates`}
                  aria-haspopup="dialog"
                  aria-expanded={openMenu === 'notifications'}
                  aria-controls={NOTIFICATIONS_MENU_ID}
                  aria-busy={notificationsLoading}
                >
                  <Bell className="header-control-icon header-icon-notifications" aria-hidden="true" />
                  <span className="header-control-label header-secondary-label">Notifications</span>
                  <span
                    className={`header-notification-count ${
                      totalNotificationCount === 0 ? 'is-empty' : ''
                    }`}
                    aria-hidden="true"
                  >
                    {visualNotificationCount}
                  </span>
                </button>

                <span className="sr-only" aria-live="polite" aria-atomic="true">
                  {pendingCount} pending requests and {unreadUpdates} unread updates
                </span>

                {openMenu === 'notifications' && (
                  <NotificationsMenu
                    id={NOTIFICATIONS_MENU_ID}
                    totalNotificationCount={totalNotificationCount}
                    pendingCount={pendingCount}
                    pendingBrd={pendingBrd}
                    pendingSharing={pendingSharing}
                    pendingTestCases={pendingTestCases}
                    unreadUpdates={unreadUpdates}
                    requestItems={requestItems}
                    updateItems={updateItems}
                    loading={notificationsLoading}
                    onRefresh={onRefreshNotifications}
                    onMarkRead={onMarkNotificationsRead}
                    onOpenRequest={(item) => {
                      setOpenMenu(null);
                      if (typeof onOpenRequestItem === 'function') onOpenRequestItem(item);
                    }}
                    onOpenUpdate={(item) => {
                      setOpenMenu(null);
                      if (typeof onOpenActivityItem === 'function') onOpenActivityItem(item);
                    }}
                    onOpenRequestCenter={() => {
                      setOpenMenu(null);
                      if (typeof onOpenRequests === 'function') onOpenRequests();
                    }}
                  />
                )}
              </div>

              <div
                className="header-menu-anchor"
                ref={profileMenuRef}
                onBlur={(event) => handleMenuBlur('profile', event)}
              >
                <button
                  ref={profileTriggerRef}
                  type="button"
                  onClick={() => toggleMenu('profile')}
                  className={`header-control header-neutral-control header-profile-trigger ${
                    openMenu === 'profile' ? 'is-open' : ''
                  }`}
                  title="Account options"
                  aria-label={`Account options for ${userLabel}`}
                  aria-haspopup="dialog"
                  aria-expanded={openMenu === 'profile'}
                  aria-controls={PROFILE_MENU_ID}
                >
                  <span className="header-profile-avatar" aria-hidden="true">{userInitials}</span>
                  <span className="header-profile-trigger-copy">
                    <strong>{userLabel}</strong>
                  </span>
                  <ChevronDown
                    className={`header-control-chevron ${
                      openMenu === 'profile' ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {openMenu === 'profile' && (
                  <ProfileMenu
                    id={PROFILE_MENU_ID}
                    userLabel={userLabel}
                    userEmail={userEmail}
                    userRoleLabel={userRoleLabel}
                    userInitials={userInitials}
                    onSecurity={() => {
                      setOpenMenu(null);
                      if (typeof onSecurity === 'function') onSecurity();
                    }}
                    onLogout={() => {
                      setOpenMenu(null);
                      if (typeof onLogout === 'function') onLogout();
                    }}
                  />
                )}
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={onLogin}
              className="header-control header-login-control"
              title="Sign in"
            >
              <LogIn className="header-control-icon" aria-hidden="true" />
              <span className="header-control-label">Sign in</span>
            </button>
          )}
        </nav>

        {openMenu && (
          <button
            type="button"
            className="header-mobile-backdrop"
            aria-label="Close open header panel"
            tabIndex={-1}
            onClick={() => setOpenMenu(null)}
          />
        )}
      </div>
    </header>
  );
};
